<template>
  <div class="container">
    <p class="header text-xl text-600 font-bold" @click="toggleOpen">
      <i :class="isOpen ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"></i>
      Equipment
    </p>
    <div v-if="isOpen" class="content">
      <div class="equipment-section">
        <div class="dropdownContainer">
          <Dropdown
            v-model="selectedType"
            :options="equipmentTypes"
            optionLabel="label"
            class="dropdown"
            @change="changeType($event.value)"
          />
        </div>

        <div class="equipment-column">
          <h3 class="equipment-title">Available Equipment</h3>
          <div
            ref="availableEquipmentContainer"
            class="available-equipment"
            @scroll="handleScroll"
          >
            <Button
              v-if="showScrollToTop"
              class="scroll-to-top"
              icon="pi pi-arrow-up"
              @click="scrollToTop"
            />
            <InputText
              v-model="filterText"
              type="text"
              placeholder="Filter equipment"
              class="w-full"
            />
            <ul>
              <li
                v-for="(item, index) in filteredAvailableEquipment"
                :key="index"
                @click="selectEquipment(item as EquipmentItem)"
              >
                {{
                  formatEquipmentByType(
                    item as EquipmentItem,
                    selectedType.value
                  )
                }}
              </li>
            </ul>
          </div>
        </div>
        <div class="equipment-column">
          <h3 class="equipment-title">Selected Equipment</h3>
          <div class="selected-equipment">
            <ul>
              <template v-for="(items, type) in selectedEquipment" :key="type">
                <div v-if="items!.length" class="equipment-items">
                  <div class="equipment-header" @click="toggleSection(type)">
                    <i
                      :class="
                        collapsedSections[type]
                          ? 'pi pi-chevron-right'
                          : 'pi pi-chevron-down'
                      "
                    ></i>
                    {{ capitalize(type) }}
                  </div>
                  <ul v-show="!collapsedSections[type]">
                    <li v-for="(item, index) in items" :key="index">
                      <i
                        class="pi pi-minus-circle"
                        @click="removeEquipment(item)"
                      ></i>
                      {{ formatEquipmentByType(item, type) }}
                    </li>
                  </ul>
                </div>
              </template>
            </ul>
          </div>
        </div>
      </div>
      <div class="buttons">
        <Button
          label="Update Equipment In Search"
          class="update-button"
          :disabled="isUpdateButtonDisabled"
          @click="updateEquipment"
        />
        <Button
          label="Clear"
          class="clear-button"
          @click="clearSelectedEquipment"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from "vue";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import type {
  Casings,
  Tubings,
  Intakes,
  Cables,
  Pumps,
  Motors,
  Seals
} from "gql/api";
import {
  useMotorsQuery,
  useTubingsQuery,
  usePumpsQuery,
  useSealsQuery,
  useCablesQuery,
  useCasingsQuery,
  useIntakesQuery
} from "gql/api";
import type {
  EquipmentItem,
  EquipmentType,
  QueryEquipment
} from "@/views/DataAnalytics/models/EquipmentSelection";
import {
  EquipSelectEnum,
  capitalize,
  defaultEquipmentSelection
} from "@/views/DataAnalytics/models/EquipmentSelection";
import { useLogger } from "@/composables/logger";
import { defaultCasingVars } from "@/models/equipment/casings/predicates";
import { defaultTubingVars } from "@/models/equipment/tubings/predicates";
import { defaultIntakeVars } from "@/models/equipment/intake/predicates";
import { defaultCableVars } from "@/models/equipment/cables/predicates";
import { defaultPumpVars } from "@/models/equipment/pumps/predicates";
import { defaultMotorVars } from "@/models/equipment/motors/predicates";
import { defaultSealVars } from "@/models/equipment/seals/predicates";

const emit = defineEmits(["update-equipment", "is-empty"]);
const EMPTY = "";
const isOpen = ref(false);
const filterText = ref("");
const showScrollToTop = ref(false);
const selectedType = ref<EquipmentType>({
  label: capitalize(EquipSelectEnum.None),
  value: EquipSelectEnum.None
});
const availableEquipmentContainer = ref<HTMLElement | null>(null);
const { info, error } = useLogger("EQUIPMENT SEARCH ERROR");

const collapsedSections = ref<Record<string, boolean>>(
  Object.keys(EquipSelectEnum).reduce(
    (acc, key) => {
      acc[key] = false;
      return acc;
    },
    {} as Record<string, boolean>
  )
);

function toggleSection(type: string) {
  collapsedSections.value[type] = !collapsedSections.value[type];
}

const equipmentTypes = ref<EquipmentType[]>([
  {
    label: capitalize(EquipSelectEnum.Casings),
    value: EquipSelectEnum.Casings
  },
  {
    label: capitalize(EquipSelectEnum.Tubings),
    value: EquipSelectEnum.Tubings
  },
  {
    label: capitalize(EquipSelectEnum.Intakes),
    value: EquipSelectEnum.Intakes
  },
  {
    label: capitalize(EquipSelectEnum.Cables),
    value: EquipSelectEnum.Cables
  },
  {
    label: capitalize(EquipSelectEnum.Pumps),
    value: EquipSelectEnum.Pumps
  },
  {
    label: capitalize(EquipSelectEnum.Motors),
    value: EquipSelectEnum.Motors
  },
  {
    label: capitalize(EquipSelectEnum.Seals),
    value: EquipSelectEnum.Seals
  },
  { label: capitalize(EquipSelectEnum.None), value: EquipSelectEnum.None }
]);
const availableEquipment = ref<QueryEquipment>([]);
const selectedEquipment = ref<Record<string, EquipmentItem[] | null>>(
  defaultEquipmentSelection()
);

function isEquipmentItem(item: unknown): item is EquipmentItem {
  return typeof item === "object" && item !== null;
}

const filteredAvailableEquipment = computed(() => {
  if (!filterText.value) {
    return availableEquipment.value;
  }
  return availableEquipment.value.filter(item => {
    if (isEquipmentItem(item)) {
      return formatEquipmentByType(item, selectedType.value.value)
        .toLowerCase()
        .includes(filterText.value.toLowerCase());
    }
    return false;
  });
});

const isUpdateButtonDisabled = computed(() => {
  return (
    selectedType.value.value === EquipSelectEnum.None ||
    flattenSelectedEquipment().length === 0
  );
});

const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};

const clearSelectedEquipment = () => {
  selectedType.value = {
    label: capitalize(EquipSelectEnum.None),
    value: EquipSelectEnum.None
  };
  selectedEquipment.value = defaultEquipmentSelection();
  updateEquipment();
};

const selectEquipment = (item: EquipmentItem) => {
  const selectedTypeValue = selectedType.value.value;
  if (
    selectedTypeValue in selectedEquipment.value &&
    selectedEquipment.value[selectedTypeValue]
  ) {
    const exists = (
      selectedEquipment.value[selectedTypeValue] as EquipmentItem[]
    ).some(selectedItem => selectedItem.id === item.id);
    if (!exists) {
      (selectedEquipment.value[selectedTypeValue] as EquipmentItem[]).push(
        item
      );
    } else {
      info(`Item with id ${item.id} already selected`);
    }
  } else {
    error(`Selected type ${selectedTypeValue} not found in selected equipment`);
  }
};

const removeEquipment = (item: EquipmentItem) => {
  const type = Object.keys(selectedEquipment.value).find(key =>
    (selectedEquipment.value[key] as EquipmentItem[]).includes(item)
  );
  if (type) {
    selectedEquipment.value[type] = (
      selectedEquipment.value[type] as EquipmentItem[]
    ).filter(i => i !== item);
  }
};

const updateEquipment = () => {
  const isEmpty = flattenSelectedEquipment().length === 0;
  emit("update-equipment", selectedEquipment.value);
  emit("is-empty", isEmpty);
};

const {
  data: casingsData,
  resume: playCasings,
  pause: stopCasings
} = useCasingsQuery({
  pause: true,
  requestPolicy: "network-only",
  ...defaultCasingVars
});
const {
  data: tubingsData,
  resume: playTubings,
  pause: stopTubings
} = useTubingsQuery({
  pause: true,
  requestPolicy: "network-only",
  ...defaultTubingVars
});
const {
  data: intakesData,
  resume: playIntakes,
  pause: stopIntakes
} = useIntakesQuery({
  pause: true,
  requestPolicy: "network-only",
  ...defaultIntakeVars
});
const {
  data: cablesData,
  resume: playCables,
  pause: stopCables
} = useCablesQuery({
  pause: true,
  requestPolicy: "network-only",
  ...defaultCableVars
});
const {
  data: pumpsData,
  resume: playPumps,
  pause: stopPumps
} = usePumpsQuery({
  pause: true,
  requestPolicy: "network-only",
  ...defaultPumpVars
});
const {
  data: motorsData,
  resume: playMotors,
  pause: stopMotors
} = useMotorsQuery({
  pause: true,
  requestPolicy: "network-only",
  ...defaultMotorVars
});
const {
  data: sealsData,
  resume: playSeals,
  pause: stopSeals
} = useSealsQuery({
  pause: true,
  requestPolicy: "network-only",
  ...defaultSealVars
});

const formatEquipmentByType = (item: EquipmentItem, type: string): string => {
  switch (type) {
    case EquipSelectEnum.Casings:
      if (item.metric_name) {
        return `${item.metric_name}, ${item.imperial_name}`;
      }
      return `${item.imperial_name}, ${item.metric_name}`;
    case EquipSelectEnum.Tubings:
      if (item.metric_name) {
        return `${item.metric_name}, ${item.imperial_name}`;
      }
      return `${item.imperial_name}, ${item.metric_name}`;
    case EquipSelectEnum.Intakes:
      return `${item.manufacturer} / ${item.intake_type}`;
    case EquipSelectEnum.Cables:
      return `${item.manufacturer} / ${item.gauge} gauge / ${item.kv} kv / ${item.construction}`;
    case EquipSelectEnum.Pumps:
      return `${item.manufacturer} / ${item.series_model}`;
    case EquipSelectEnum.Motors:
      return `${item.manufacturer} / ${item.part_number} / ${item.nameplate_horsepower} HP / ${item.nameplate_voltage} V / ${item.nameplate_current} A`;
    case EquipSelectEnum.Seals:
      return `${item.manufacturer} / ${item.configuration} / OD ${item.outer_diameter_inches} inches  / ${item.length_inches} inches`;
    default:
      return EMPTY;
  }
};

function flattenSelectedEquipment(): EquipmentItem[] {
  return Object.values(selectedEquipment.value).flat() as EquipmentItem[];
}

function changeType(selected: EquipmentType) {
  stopCasings();
  stopTubings();
  stopIntakes();
  stopCables();
  stopPumps();
  stopMotors();
  stopSeals();
  switch (selected.value) {
    case EquipSelectEnum.Casings:
      playCasings();
      break;
    case EquipSelectEnum.Tubings:
      playTubings();
      break;
    case EquipSelectEnum.Intakes:
      playIntakes();
      break;
    case EquipSelectEnum.Cables:
      playCables();
      break;
    case EquipSelectEnum.Pumps:
      playPumps();
      break;
    case EquipSelectEnum.Motors:
      playMotors();
      break;
    case EquipSelectEnum.Seals:
      playSeals();
      break;
    case EquipSelectEnum.None:
      availableEquipment.value = [];
      break;
    default:
  }
}

onMounted(() => {
  if (availableEquipmentContainer.value) {
    availableEquipmentContainer.value.addEventListener("scroll", handleScroll);
  }
});

onUnmounted(() => {
  if (availableEquipmentContainer.value) {
    availableEquipmentContainer.value.removeEventListener(
      "scroll",
      handleScroll
    );
  }
});

function handleScroll() {
  if (availableEquipmentContainer.value) {
    showScrollToTop.value = availableEquipmentContainer.value.scrollTop > 100;
  }
}

function scrollToTop() {
  if (availableEquipmentContainer.value) {
    availableEquipmentContainer.value.scrollTop = 0;
  }
}

watch(
  [
    casingsData,
    tubingsData,
    intakesData,
    cablesData,
    pumpsData,
    motorsData,
    sealsData
  ],
  ([
    newCasings,
    newTubings,
    newIntakes,
    newCables,
    newPumps,
    newMotors,
    newSeals
  ]) => {
    switch (selectedType.value.value) {
      case EquipSelectEnum.Casings:
        availableEquipment.value = (newCasings?.casings || []) as Casings[];
        break;
      case EquipSelectEnum.Tubings:
        availableEquipment.value = (newTubings?.tubings || []) as Tubings[];
        break;
      case EquipSelectEnum.Intakes:
        availableEquipment.value = (newIntakes?.intakes || []) as Intakes[];
        break;
      case EquipSelectEnum.Cables:
        availableEquipment.value = (newCables?.cables || []) as Cables[];
        break;
      case EquipSelectEnum.Pumps:
        availableEquipment.value = (newPumps?.pumps ||
          []) as unknown as Pumps[];
        break;
      case EquipSelectEnum.Motors:
        availableEquipment.value = (newMotors?.motors || []) as Motors[];
        break;
      case EquipSelectEnum.Seals:
        availableEquipment.value = (newSeals?.seals || []) as Seals[];
        break;
      default:
        availableEquipment.value = [];
    }
  },
  { deep: true }
);
</script>

<style lang="scss" scoped>
.header {
  cursor: pointer;
  display: flex;
  align-items: center;

  i {
    margin-right: 8px;
    border-radius: 30px;
    padding: 5px;
  }
}

.content {
  margin-top: 10px;
}
.equipment-items {
  margin-bottom: 10px;
}

.equipment-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.available-equipment,
.selected-equipment {
  position: relative;
  padding: 20px;
  padding-top: 10px;
  border: 1px solid #ddd;
  height: 18rem;
  overflow-y: scroll;
  ul {
    list-style-type: none;
    padding: 0;
  }

  li {
    padding: 5px;
    margin-bottom: 5px;
    border-radius: 3px;
  }

  .scroll-to-top {
    position: sticky;
    right: 10px;
    z-index: 1;
    top: 85%;
    left: 100%;
    &:hover {
      opacity: 0.8;
    }
  }
  &::-webkit-scrollbar {
    width: 12px; /* Width of the scrollbar */
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1; /* Background of the scrollbar track */
  }

  &::-webkit-scrollbar-thumb {
    background: #007bff; /* Blue color for the scrollbar thumb */
    border-radius: 10px; /* Rounded corners for the scrollbar thumb */

    &:hover {
      background: #0056b3; /* Darker blue when hovering over the scrollbar thumb */
    }
  }
}

i {
  cursor: pointer;
  &:hover {
    opacity: 0.3;
  }
  &.pi-minus-circle {
    color: $spyglass-red;
  }
}

.available-equipment li {
  cursor: pointer;
  &:hover {
    opacity: 0.3;
  }
}

.dropdown {
  min-width: 140px;
}

.equipment-column {
  width: 40%;
  position: relative;
}

.buttons {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.update-button,
.clear-button {
  padding: 10px 20px;
}
</style>
